// src/components/documents/DocumentsPage.tsx
import { useEffect, useState, useMemo } from "react"
import { useDocumentStore } from "@/store/documentStore"
import { useAuthStore } from "@/store/authStore"
import { useNotificationStore } from "@/store/notificationStore"
import { FileText, Plus, Search, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { DocumentForm, DocumentDetailModal, DocumentEditor } from "@/components/documents"
import { format } from "date-fns"
import { ru } from "date-fns/locale"
import { motion } from "framer-motion"
import { deleteDoc, doc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { toast } from "@/hooks/use-toast"

// === ДИЗАЙН-СЛОЙ ===
import { documentsPageTheme } from "./ui/DocumentsPageUI"

const statusColors: Record<string, string> = {
  draft: "bg-gray-600",
  pending: "bg-amber-600",
  approved: "bg-emerald-600",
  rejected: "bg-rose-600",
  signed: "bg-violet-600",
  archived: "bg-slate-700",
}

export default function DocumentsPage() {
  const {
    documents,
    myDocuments,
    documentsForApproval,
    loading,
    init,
    cleanup,
  } = useDocumentStore()

  const { user } = useAuthStore()
  const { markDocumentAsRead } = useNotificationStore()

  const [tab, setTab] = useState<"all" | "my" | "approval">("all")
  const [search, setSearch] = useState("")
  const [showForm, setShowForm] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [selectedEditorDocId, setSelectedEditorDocId] = useState<string | null>(null)

  const [typeFilter, setTypeFilter] = useState<"all" | "report" | "act" | "order" | "memo" | "contract" | "other">("all")

  const typeLabels: Record<string, { label: string; color: string }> = {
    report: { label: "Отчёт", color: "bg-blue-600" },
    act: { label: "Акт", color: "bg-emerald-600" },
    order: { label: "Приказ", color: "bg-violet-600" },
    memo: { label: "Служебная записка", color: "bg-amber-600" },
    contract: { label: "Договор", color: "bg-pink-600" },
    other: { label: "Другое", color: "bg-slate-600" },
  }

  useEffect(() => {
    init()
    return cleanup
  }, [init, cleanup])

  const currentList = tab === "my"
    ? myDocuments
    : tab === "approval"
      ? documentsForApproval
      : documents

  const filtered = currentList.filter(doc =>
    (
      doc.title.toLowerCase().includes(search.toLowerCase()) ||
      (doc.description || "").toLowerCase().includes(search.toLowerCase())
    ) &&
    (typeFilter === "all" || doc.type === typeFilter)
  )

  const userIdStr = String(user?.id || user?.uid || "")

  const documentsWithUnread = useMemo(() => {
    return filtered.map((doc) => ({
      ...doc,
      unreadMessagesCount: Number(doc.unreadMessagesBy?.[userIdStr] || 0),
      hasGreenMarker: Array.isArray(doc.unreadBy) && doc.unreadBy.includes(userIdStr),
    }))
  }, [filtered, userIdStr])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-purple-400 animate-pulse text-3xl">Загрузка документов...</div>
      </div>
    )
  }

  const handleDelete = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm("Вы уверены, что хотите удалить этот документ?")) return

    try {
      await deleteDoc(doc(db, "documents", docId))
      toast({ title: "Успех", description: "Документ удалён" })
    } catch (err) {
      console.error("Ошибка удаления:", err)
      toast({ title: "Ошибка", description: "Не удалось удалить документ", variant: "destructive" })
    }
  }

  const safeFormatDate = (dateValue: any) => {
    if (!dateValue) return "—";
    try {
      const date = dateValue instanceof Date 
        ? dateValue 
        : dateValue?.toDate 
          ? dateValue.toDate() 
          : new Date(dateValue);
      
      if (isNaN(date.getTime())) return "—";
      
      return format(date, "dd MMM yyyy, HH:mm", { locale: ru });
    } catch {
      return "—";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={documentsPageTheme.mainContainer}
    >
      <div className={documentsPageTheme.header}>
        <div>
          <h1 className={documentsPageTheme.titleContainer}>
            Документооборот ОРВ
          </h1>
          <p className={documentsPageTheme.subtitle}>Создание, согласование, подписание</p>
        </div>

        <Button
          onClick={() => setShowForm(true)}
          className={documentsPageTheme.newButton}
        >
          <Plus className="mr-2 h-5 w-5" />
          Новый документ
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className={documentsPageTheme.tabsContainer}>
          {(["all", "my", "approval"] as const).map((t) => (
            <Button
              key={t}
              variant={tab === t ? "default" : "ghost"}
              size="sm"
              onClick={() => setTab(t)}
              className={documentsPageTheme.tabButton(tab === t)}
            >
              {t === "all" ? "Все" : t === "my" ? "Мои" : "На согласовании"}
              {t === "approval" && documentsForApproval.length > 0 && (
                <Badge className={documentsPageTheme.approvalBadge}>
                  {documentsForApproval.length}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        <div className={documentsPageTheme.filterContainer}>
          <span className={documentsPageTheme.filterLabel}>Тип:</span>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
            <SelectTrigger className={documentsPageTheme.selectTrigger}>
              <SelectValue placeholder="Все типы" />
            </SelectTrigger>
            <SelectContent className={documentsPageTheme.selectContent}>
              <SelectItem value="all" className={documentsPageTheme.selectItem}>Все типы</SelectItem>
              <SelectItem value="report" className={documentsPageTheme.selectItem}>Отчёт</SelectItem>
              <SelectItem value="act" className={documentsPageTheme.selectItem}>Акт</SelectItem>
              <SelectItem value="order" className={documentsPageTheme.selectItem}>Приказ</SelectItem>
              <SelectItem value="memo" className={documentsPageTheme.selectItem}>Служебная записка</SelectItem>
              <SelectItem value="contract" className={documentsPageTheme.selectItem}>Договор</SelectItem>
              <SelectItem value="other" className={documentsPageTheme.selectItem}>Другое</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={documentsPageTheme.searchContainer}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-400" />
          <Input
            placeholder="Поиск по названию..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={documentsPageTheme.searchInput}
          />
        </div>
      </div>

      <div className={documentsPageTheme.tableWrapper}>
        <Table>
          <TableHeader className={documentsPageTheme.tableHeader}>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead className={documentsPageTheme.tableHeaderCell}>Название</TableHead>
              <TableHead className={documentsPageTheme.tableHeaderCell}>Теги</TableHead>
              <TableHead className={documentsPageTheme.tableHeaderCell}>Статус</TableHead>
              <TableHead className="hidden md:table-cell text-purple-200 text-3xl font-semibold py-6">Создан</TableHead>
              <TableHead className={documentsPageTheme.tableHeaderCell}>История</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documentsWithUnread.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className={documentsPageTheme.emptyCell}>
                  {search || typeFilter !== "all" ? "Ничего не найдено" : "Документов пока нет"}
                </TableCell>
              </TableRow>
            ) : (
              documentsWithUnread.map((doc) => (
                <TableRow
                  key={doc.id}
                  className={documentsPageTheme.tableRow}
                  onClick={() => {
                    markDocumentAsRead(doc.id)
                    setSelectedDocId(doc.id)
                  }}
                >
                  <TableCell className="w-12 relative">
                    {doc.hasGreenMarker && (
                      <div className={documentsPageTheme.greenMarker}>
                        <div className={documentsPageTheme.greenPulse}>
                          <div className={documentsPageTheme.greenDot} />
                        </div>
                      </div>
                    )}

                    {doc.unreadMessagesCount > 0 && (
                      <Badge className={documentsPageTheme.unreadBadge}>
                        {doc.unreadMessagesCount > 99 ? "99+" : doc.unreadMessagesCount}
                      </Badge>
                    )}

                    {!doc.hasGreenMarker && doc.unreadMessagesCount === 0 && <div className="w-8 h-8" />}
                  </TableCell>

                  <TableCell className="font-medium text-white text-2xl">
                    <div className="line-clamp-2">{doc.title}</div>
                    {doc.fileName && (
                      <div className="text-lg text-purple-400 mt-1 truncate">{doc.fileName}</div>
                    )}
                  </TableCell>

                  <TableCell>
                    <Badge className={documentsPageTheme.typeBadge(typeLabels[doc.type]?.color || "bg-slate-600")}>
                      {typeLabels[doc.type]?.label || doc.type}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge className={documentsPageTheme.statusBadge(statusColors[doc.status])}>
                      {doc.status === "pending" ? "На согласовании" :
                       doc.status === "draft" ? "Черновик" :
                       doc.status === "approved" ? "Согласовано" :
                       doc.status === "rejected" ? "На доработку" :
                       doc.status === "signed" ? "Подписано" : "Архив"}
                    </Badge>
                  </TableCell>

                  <TableCell className="hidden md:table-cell text-purple-300 text-3xl">
                    {safeFormatDate(doc.createdAt)}
                  </TableCell>

                  <TableCell className={documentsPageTheme.historyCell}>
                    <div className="space-y-1 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                      {doc.history?.length > 0 ? (
                        doc.history.slice(0, 3).map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-2xl">
                            <span className="text-purple-400">•</span>
                            <span>{entry.userName}</span>
                            <span className="text-purple-500">:</span>
                            <span>{entry.action}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-purple-500 text-2xl">Нет истории</span>
                      )}
                    </div>
                  </TableCell>

                  {/* === КРУПНЫЕ КНОПКИ (исправлено) === */}
                  <TableCell className="flex items-center gap-4">
                    <Button
                      variant="ghost"
                      className="!h-14 !w-14 text-purple-400 hover:text-purple-200 hover:bg-purple-900/30 flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); setSelectedEditorDocId(doc.id); }}
                    >
                      <FileText className="h-7 w-7" />
                    </Button>

                    {String(user?.id) === "4" && (
                      <Button
                        variant="ghost"
                        className="!h-14 !w-14 text-red-400 hover:text-red-300 hover:bg-red-900/30 flex-shrink-0"
                        onClick={(e) => handleDelete(doc.id, e)}
                      >
                        <Trash2 className="h-7 w-7" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Модалки */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className={documentsPageTheme.modalForm}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Новый документ
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              Заполните поля, прикрепите файл и отправьте на согласование
            </DialogDescription>
          </DialogHeader>
          <DocumentForm onClose={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      {selectedDocId && (
        <Dialog open={!!selectedDocId} onOpenChange={() => setSelectedDocId(null)}>
          <DialogContent className={documentsPageTheme.modalDetail}>
            <DocumentDetailModal docId={selectedDocId} onClose={() => setSelectedDocId(null)} />
          </DialogContent>
        </Dialog>
      )}

      {selectedEditorDocId && (
        <Dialog open={!!selectedEditorDocId} onOpenChange={() => setSelectedEditorDocId(null)}>
          <DialogContent className={documentsPageTheme.modalEditor}>
            <DocumentEditor docId={selectedEditorDocId} onClose={() => setSelectedEditorDocId(null)} />
          </DialogContent>
        </Dialog>
      )}
    </motion.div>
  )
}